import { Observable } from "rxjs/Rx";
import { TranslateService } from "@ngx-translate/core";
import { Helper } from "./../../../shared/helper";
import { Component, OnInit, Input, ViewChild, OnChanges } from "@angular/core";
import { Chart } from "chart.js";
import { Logger, MULTICITY_PARAM, NAME_REGION } from "app/shared";
import { MultiLegendComponent } from "../multi-legend/multi-legend.component";

@Component({
  selector: "htm-chart",
  templateUrl: "./chart.component.html",
  styleUrls: ["./chart.component.scss"]
})
export class ChartComponent implements OnInit, OnChanges {
  @ViewChild("canvas") canvas;
  @ViewChild(MultiLegendComponent) multiLegendComponent: MultiLegendComponent;
  @Input() type;
  @Input() chart_id;
  @Input() options;
  @Input() chartData;
  @Input() unit;
  @Input() value_type;
  @Input() indicator;
  @Input() totals_in_tooltip; // true if we want to display the totals in the tooltip
  private chart: Chart;
  private yAxeLabel: string;
  private tot_ren_dougnhut;
  private unit_ren_doughnut: String;
  private localOptions;
  private multiCityParam = MULTICITY_PARAM;

  constructor(
    private logger: Logger,
    private helper: Helper,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.logger.log("ChartComponent/ngOnInit()");
  }
  ngOnChanges(changes) {
    if (changes.options && changes.options.currentValue) {
      this.localOptions = JSON.parse(JSON.stringify(this.options));
      if (this.type === "bar") {
        this.localOptions.scales.yAxes[0]["ticks"] = {
          fontColor: "black",
          fontSize: 12,
          callback: value => {
            return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
          }
        };
      }
    }
    if (
      !this.helper.isNullOrUndefined(changes.chartData) &&
      !this.helper.isNullOrUndefined(changes.chartData.currentValue)
    ) {
      this.createChart();
    }
  }

  createChart() {
    this.resetChart();
    const ctx = this.canvas.nativeElement.getContext("2d");
    if (!this.helper.isNullOrUndefined(this.localOptions)) {
      // translate region name in labels for multi region barcharts
      if (this.type == "bar" && this.multiCityParam == "True") {
        for (let i = 0; i < this.chartData.labels.length; i++){
          this.chartData.labels[i] = this.translateService.instant(
            `CITIES.${this.chartData.labels[i]}`
          );
        }
      }
      

      /* Remove label for dataset when only one city is displayed on the chart*/
      // if (this.chartData["labels"] && this.chartData["labels"].length == 1) {
      //   this.chartData["labels"] = [""];
      // }
      /* Convert unit and values when needed for displaying like indicated in config file and 
      not from data in database*/
      var i;
      if (this.type == "bar") {
        if (
          (this.chartData["indicator"] && this.indicator == "energy") ||
          this.chart_id.indexOf("energy") != -1
        ) {
          var y_label_type = "Y_LABEL_NAME";
        } else if (
          (this.chartData["indicator"] && this.indicator == "co2") ||
          this.chart_id.indexOf("co2") != -1
        ) {
          var y_label_type = "Y_LABEL_NAME_CO2";
        }
        this.yAxeLabel = this.translateService.instant(
          `DASHBOARD.CHARTS.${this.chart_id}.${y_label_type}`
        );
        var unit_from_config = this.helper.getUnitIdFromFilterValue(
          this.value_type,
          this.unit[this.indicator]
        );
        // override unit if filter is not used
        var unit_to_use = this.unit[this.indicator];
        if (!this.chartData["indicator"]) {
          if (this.chart_id.indexOf("energy") != -1) {
            unit_to_use = this.unit["energy"];
          } else if (this.chart_id.indexOf("co2") != -1) {
            unit_to_use = this.unit["co2"];
          }
        }
        // override unit if filter is not used
        if (!this.chartData["value_type"]) {
          if (this.chart_id.indexOf("pourcent") != -1) {
            unit_from_config = this.helper.getUnitIdFromFilterValue(
              "pourcent",
              unit_to_use
            );
            //
          } else if (this.chart_id.indexOf("habitant") != -1) {
            unit_from_config = this.helper.getUnitIdFromFilterValue(
              "habitant",
              unit_to_use
            );
            //
          } else {
            // default is absolute
            unit_from_config = this.helper.getUnitIdFromFilterValue(
              "absolute",
              unit_to_use
            );
            //
          }
        }
        this.yAxeLabel += this.translateService.instant(
          `UNITS.${unit_from_config["translate_unit"]}`
        );
        this.localOptions["scales"]["yAxes"][0]["scaleLabel"][
          "labelString"
        ] = this.yAxeLabel;
      }

      // override unit if filter is not used
      var unit_to_use = this.unit[this.indicator];
      if (!this.chartData["indicator"]) {
        if (this.chart_id.indexOf("energy") != -1) {
          unit_to_use = this.unit["energy"];
        } else if (this.chart_id.indexOf("co2") != -1) {
          unit_to_use = this.unit["co2"];
        }
      }
      if (
        (this.chartData["value_type"] && this.value_type == "pourcent") ||
        this.chart_id.indexOf("pourcent") != -1
      ) {
        var data_unit = "pct";
      } else if (
        (this.chartData["value_type"] && this.value_type == "habitant") ||
        this.chart_id.indexOf("habitant") != -1
      ) {
        var data_unit = `inhabitant_${this.chartData["datasets"][0][
          "unit"
        ][0]}`;
        var unit_from_config = `inhabitant_${unit_to_use}`;
      } else if (
        (this.chartData["value_type"] && this.value_type == "absolute") ||
        this.chart_id.indexOf("absolute") != -1
      ) {
        var data_unit = `${this.chartData["datasets"][0]["unit"][0]}`;
        var unit_from_config = `${unit_to_use}`;
      }

      for (i = 0; i < this.chartData["datasets"].length; i++) {
        var j;
        for (j = 0; j < this.chartData["datasets"][i]["unit"].length; j++) {
          if (data_unit == "pct") {
            this.chartData["datasets"][i]["unit"][j] = this.helper.getUnitInfo(
              data_unit
            )["translate_unit"];
          } else {
            if ((['energy.inhabitant_mwh', 'mwh_inhabitant', 'mwh', 'energy.abs_mwh'].includes(this.chartData["datasets"][0]["unit"][0])) && (['inhabitant_mwh', 'mwh'].includes(unit_from_config))) {
              var temporary_factor = 1;
            } else 
            if (this.indicator == "energy") {
              var temporary_factor = 1000;
            } else {
              var temporary_factor = 1;
            }
            let unit_to_display = this.helper.getUnitInfo(unit_from_config);
            if((unit_from_config.toLowerCase() == 'inhabitant_tco2') && (data_unit.toLowerCase() == 'inhabitant_tco2_inhabitant')){
              temporary_factor = 1;
            }
            this.chartData["datasets"][i]["data"][j] *= temporary_factor;
            this.chartData["datasets"][i]["unit"][j] =
              unit_to_display["translate_unit"];
            // [this.chartData["datasets"][i]["data"][j], this.chartData["datasets"][i]["unit"][j]] = this.helper.convertTooBigValue(this.chartData["datasets"][i]["data"][j], this.chartData["datasets"][i]["unit"][j]);
          }
        }
      }
      if (
        this.chartData["labels"].length > 1 &&
        this.chartData["labels"].includes("")
      ) {
        this.chartData["labels"] = this.chartData["labels"].filter(e => {
          return e !== "";
        });
      }

      /* 
      convert the total values using the previously defined conversion factor 
      this implementation is a quick fix and should be changed when the unit handling is updated in the backend
       */
      const convertedTotals = this.chartData.totals.map(dataset_total => {
        return dataset_total.map(current_total => {
          return current_total * temporary_factor; // this will result in nan if temporary factor is not defined, i.e. for %
        });
      });
      this.chartData.totals = convertedTotals;

      this.localOptions.tooltips.callbacks.label = (item, data) => {
        let unit;
        let result;
        let datalabel;
        let translate_var = this.value_type;
        if (this.type === "bar") {
          datalabel = data.datasets[item.datasetIndex].label;
          result = item.yLabel;
          unit = data.datasets[item.datasetIndex].unit[item.index];
          [result, unit] = this.helper.convertTooBigValue(
            item.yLabel,
            data.datasets[item.datasetIndex].unit[item.index]
          );
        } else if (this.type === "doughnut") {
          datalabel = data.labels[item.index];
          [result, unit] = this.helper.convertTooBigValue(
            data.datasets[0].data[item.index],
            data.datasets[item.datasetIndex].unit[item.index]
          );
        } else {
          datalabel = data.labels[item.index];
          result = data.datasets[0].data[item.index];
          unit = data.datasets[0].unit[item.index];
        }
        translate_var = unit;

        /*  else {
          translate_var = unit + '.energy_absolute';
        } */
        return [
          `${this.translateService.instant("DATA." + datalabel)} :`,
          `${this.helper.getThousandSeparatorFromNumber(result)} ` +
            this.translateService.instant("UNITS." + translate_var)
        ];
      };
      this.localOptions.animation.onComplete = () => {
        if (this.multiLegendComponent) {
          this.multiLegendComponent.update();
        }
      };
    }

    // configure tooltip footer to display total values
    // don't display if not specified in configuration and for % (total will always be 100%)
    if (this.value_type != "pourcent" && this.totals_in_tooltip) {
      this.localOptions.tooltips.callbacks.footer = (item, data) => {
        // hovered data indices
        const datasetIndex = item[0].datasetIndex;
        const dataIndex = item[0].index;

        // raw values
        let total = this.chartData.totals[datasetIndex][dataIndex];
        let unit = data.datasets[datasetIndex].unit[dataIndex];

        // format values
        [total, unit] = this.helper.convertTooBigValue(total, unit);
        total = this.helper.getThousandSeparatorFromNumber(total);
        unit = this.translateService.instant("UNITS." + unit);
        const total_translation = this.translateService.instant(
          `DASHBOARD.CHARTS.${this.chart_id}.TOTAL_TOOLTIP`
        );

        return [total_translation, total + " " + unit];
      };
    } else {
      // disable the footer callback to not display the totals
      this.localOptions.tooltips.callbacks.footer = (item, data) => {
        return [];
      };
    }

    this.chart = new Chart(ctx, {
      type: this.type,
      data: this.chartData,
      options: this.localOptions
    });
  }

  resetChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = void 0;
    }
  }
}
